import { ArrowRight, MapPinned, Smartphone, Wifi } from "lucide-react"
import { Link } from "react-router-dom"
import { Logo } from "@/components/logo"
import { Button } from "@/components/ui/button"

const legalLinks = [
  { href: "/privacy-policy", label: "Privacy" },
  { href: "/terms-of-use", label: "Terms" },
  { href: "/refund-policy", label: "Refunds" },
]

const liveProductDetails = [
  { icon: MapPinned, label: "Destination plans" },
  { icon: Smartphone, label: "Digital setup" },
  { icon: Wifi, label: "Travel data" },
]

const focusAreas = ["Connectivity", "Money movement", "Identity", "Operations"]

export default function HomePage() {
  return (
    <div className="home-shell min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-3 sm:px-6 sm:py-4 lg:px-8 lg:py-5">
        <header className="home-panel flex items-center py-3">
          <Link to="/" className="transition-opacity hover:opacity-70">
            <Logo variant="main" size="md" />
          </Link>
        </header>

        <main className="grid flex-1 gap-8 py-6 sm:gap-10 sm:py-8 lg:min-h-0 lg:grid-cols-[minmax(0,1.2fr)_24rem] lg:items-center lg:gap-12 lg:py-6">
          <section className="home-panel home-hero flex flex-col justify-center">
            <div className="max-w-3xl">
              <h1 className="display-title max-w-3xl text-foreground">
                Tools for life
                <em className="block">across borders.</em>
              </h1>
              <p className="lede mt-4 sm:mt-5">
                MonraLabs builds clear, practical software for international life: the connectivity, admin, and everyday logistics that get harder across borders.
              </p>
              <div className="mt-5 flex max-w-2xl flex-wrap gap-2 sm:mt-6">
                {focusAreas.map((area) => (
                  <span
                    key={area}
                    className="rounded-full border border-border px-2.5 py-1 text-[13px] font-medium text-muted-foreground"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </section>

          <aside className="lg:min-h-0">
            <section className="home-panel rounded-2xl bg-card p-4 shadow-[0_6px_16px_rgba(0,0,0,0.12)] sm:p-5">
              <p className="text-[13px] font-semibold text-foreground">Latest product</p>
              <p className="mt-4 font-display text-[1.55rem] font-semibold leading-none tracking-[-0.04em] text-foreground">
                Monra eSIM
              </p>
              <p className="mt-1.5 text-[13px] font-medium text-muted-foreground">Travel connectivity</p>
              <p className="mt-2.5 text-[13px] leading-6 text-muted-foreground">
                Browse destination plans, install a digital SIM on your phone, and get travel data when you land — without swapping a physical card.
              </p>
              <div className="mt-4 grid gap-1.5">
                {liveProductDetails.map((item) => (
                  <div key={item.label} className="flex items-center gap-3 rounded-2xl bg-muted px-3 py-2.5 text-[13px] font-semibold text-foreground">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-background text-esim-deep">
                      <item.icon className="h-4 w-4" />
                    </span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
              <Button asChild className="mt-4 w-full bg-esim text-esim-foreground hover:bg-esim-hover">
                <Link to="/esim">
                  Open product page <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </section>

            <section className="home-panel mt-5 px-1 pb-2 sm:px-0">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-muted-foreground">
                <a href="mailto:support@monralabs.com" className="font-semibold text-foreground transition-opacity hover:opacity-70">
                  support@monralabs.com
                </a>
                {legalLinks.map((link) => (
                  <a key={link.href} href={link.href} className="transition-colors hover:text-foreground hover:underline">
                    {link.label}
                  </a>
                ))}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                © {new Date().getFullYear()} Monra Labs Ltd.
              </p>
            </section>
          </aside>
        </main>
      </div>
    </div>
  )
}
